/**
 * Studio project defaults, tags, timeline modes, and universal capabilities.
 * User-facing organization uses {@link sanitizeProjectTags} → `projectTags` string array.
 */

import { deleteField } from 'firebase/firestore';

/** Max tags per project; each tag trimmed length capped */
export const MAX_PROJECT_TAGS = 20;
export const MAX_PROJECT_TAG_LENGTH = 32;

/** Below-rail timeline sub-label — Firestore `timelineSubLabelMode` */
export const TIMELINE_SUB_LABEL_MODES = Object.freeze(['days', 'version', 'coordinates', 'custom']);

/**
 * Normalize arbitrary input into a stable tag list for Firestore.
 * @param {unknown} raw
 * @returns {string[]}
 */
export function sanitizeProjectTags(raw) {
  if (raw == null) return [];
  const arr = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(/[,;]+/) : [];
  const out = [];
  const seen = new Set();
  for (const item of arr) {
    if (out.length >= MAX_PROJECT_TAGS) break;
    const s = typeof item === 'string' ? item.trim().slice(0, MAX_PROJECT_TAG_LENGTH) : '';
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/**
 * @returns {'version'} universal default for new projects
 */
export function getDefaultTimelineSubLabelMode() {
  return 'version';
}

/**
 * @param {unknown} raw
 * @returns {'days'|'version'|'coordinates'|'custom'}
 */
export function normalizeTimelineSubLabelMode(raw) {
  const allowed = new Set(TIMELINE_SUB_LABEL_MODES);
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (s === 'none') return 'days';
  if (allowed.has(s)) return s;
  return getDefaultTimelineSubLabelMode();
}

/**
 * Universal capabilities — all non-removed studio features enabled for every project.
 * @typedef {Object} ProjectCapabilities
 */
export function getProjectCapabilities() {
  return {
    sceneTimeline: true,
    timelinesFeature: true,
    shareTimelineCuratedOrder: true,
    mapInTimeline: true,
    mapProminent: true,
    gpsOverlayDefaultOn: true,
    splatOverMapOpacityEnabled: true,
    rfiPinsInViewerDefault: true,
    dailyTimelineFraming: true,
    yearlyMonthlyTimelineFraming: true,
    iterativeVersionTimeline: true,
    narrativeTimelineFraming: false,
    measurementPriority: true,
    defaultMeasurementUnit: 'mm',
    presentationHideTechnical: false,
    animationTourEnabled: true,
    cinematicPlayEnabled: false,
    videoExportEnabled: false,
    timelineModeLocked: false,
    pdfReportExport: true,
    gridInViewerDefault: true,
    mapTabEnabled: true,
    mapOverlaysEnabled: true,
  };
}

/**
 * Firestore defaults for new project docs.
 * @returns {Record<string, unknown>}
 */
export function getDefaultProjectSettings() {
  return {
    projectTags: [],
    timelineLabelMode: 'version',
    timelineSubLabelMode: getDefaultTimelineSubLabelMode(),
    timelineAutoAdd: true,
    measurementUnitPref: 'mm',
    splatOverMapOpacity: 0.85,
  };
}

/**
 * Defaults when the user picks a damage-documentation persona at onboarding.
 * @returns {Record<string, unknown>}
 */
export function getDamageIncidentProjectDefaults() {
  return {
    ...getDefaultProjectSettings(),
    projectTags: sanitizeProjectTags(['incident', 'insurance', 'damage']),
    timelineSubLabelMode: 'days',
    measurementUnitPref: 'm',
  };
}

/**
 * Client migration: backfill defaults, remove deprecated `projectType`.
 * @param {object} data
 * @returns {{ patch: Record<string, unknown>, didChange: boolean }}
 */
export function buildProjectMigrationPatch(data) {
  if (!data || typeof data !== 'object') {
    return { patch: {}, didChange: false };
  }
  const patch = {};
  let didChange = false;

  if (data.projectType !== undefined) {
    patch.projectType = deleteField();
    didChange = true;
  }

  const defaults = getDefaultProjectSettings();

  if (data.projectTags === undefined) {
    patch.projectTags = [];
    didChange = true;
  }

  if (data.timelineLabelMode == null || data.timelineLabelMode === '') {
    patch.timelineLabelMode = defaults.timelineLabelMode;
    didChange = true;
  }
  if (data.timelineSubLabelMode == null || data.timelineSubLabelMode === '') {
    patch.timelineSubLabelMode = defaults.timelineSubLabelMode;
    didChange = true;
  }
  if (data.timelineAutoAdd === undefined) {
    patch.timelineAutoAdd = defaults.timelineAutoAdd;
    didChange = true;
  }
  if (data.measurementUnitPref == null || data.measurementUnitPref === '') {
    patch.measurementUnitPref = defaults.measurementUnitPref;
    didChange = true;
  }
  if (data.splatOverMapOpacity == null && defaults.splatOverMapOpacity != null) {
    patch.splatOverMapOpacity = defaults.splatOverMapOpacity;
    didChange = true;
  }

  return { patch, didChange };
}
